// src/Components/CourseForm.js
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import api from '../api/api.js';
import { courseSchema } from '../lib/validations';
import Button from './ui/Button.jsx';
import InfoBox from './ui/InfoBox.jsx';
import f from '../styles/forms.module.css';

const CourseForm = ({ form, resetForm, routeOptions, activatedRouteIds = new Set(), onSuccess }) => {
  const isEditing = !!form.id;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(courseSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      route_id: '',
      status:   'programmed',
    },
  });

  useEffect(() => {
    reset({
      route_id: form.route_id ? String(form.route_id) : '',
      status:   form.status || 'programmed',
    });
  }, [form.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [serverError, setServerError] = useState('');

  const onSubmit = async (data) => {
    setServerError('');
    const payload = {
      ID_Route: parseInt(data.route_id),
      Status:   data.status || 'programmed',
    };

    try {
      if (isEditing) {
        await api.put(`/course/${form.id}`, payload);
        if (onSuccess) onSuccess(form.id, data.status);
      } else {
        await api.post('/course', payload);
        if (onSuccess) onSuccess(null, null); // null = re-fetch lista completa
      }
      resetForm();
    } catch (err) {
      setServerError(
        err.response?.data?.errors?.[0]?.message ||
        err.response?.data?.error ||
        'Error al guardar el viaje'
      );
    }
  };

  return (
    <div className={f.card}>
      {/* Header */}
      <div className={f.cardHeader}>
        <div className={f.cardTitleGroup}>
          <h3 className={f.cardTitle}>{isEditing ? 'Editar Estado del Viaje' : 'Activar Ruta'}</h3>
          <p className={f.cardDesc}>
            {isEditing
              ? 'Cambia el estado del viaje en curso.'
              : 'Selecciona una ruta para activarla y hacerla disponible para reservas.'}
          </p>
        </div>
        <span className={f.badgeAdmin}>Admin</span>
      </div>
      <hr className={f.divider} />

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {serverError && <div className={f.serverError} role="alert">{serverError}</div>}

        <div className={f.section}>
          <span className={f.sectionLabel}>Configuración del Viaje</span>

          <InfoBox>
            La ruta ya contiene fecha, hora, bus y toda la informacion del viaje. Solo debes seleccionarla para activarla.
          </InfoBox>

          {!isEditing && routeOptions.filter(o => !activatedRouteIds.has(Number(o.value))).length === 0 && (
            <div className={f.serverError} style={{ marginTop: 12 }}>
              No hay rutas disponibles para activar. Todas las rutas activas ya tienen un viaje programado.
            </div>
          )}

          <div className={f.grid2} style={{ marginTop: '12px' }}>
            {/* Ruta */}
            <div className={f.field}>
              <label htmlFor="course-route" className={`${f.label} ${f.required}`}>Ruta</label>
              <select
                id="course-route"
                {...register('route_id')}
                className={`${f.select} ${errors.route_id ? f.inputError : ''}`}
                aria-describedby={errors.route_id ? 'course-route-error' : undefined}
                aria-invalid={!!errors.route_id}
                disabled={isEditing}
              >
                <option value="">Seleccione una ruta</option>
                {routeOptions
                  .filter(o => !activatedRouteIds.has(Number(o.value)))
                  .map(opt => (
                    <option key={opt.value} value={String(opt.value)}>
                      {opt.label}
                    </option>
                  ))
                }
              </select>
              {errors.route_id && (
                <span id="course-route-error" className={f.fieldError} role="alert">
                  {errors.route_id.message}
                </span>
              )}
              {isEditing && (
                <span className={f.fieldHint}>La ruta no puede cambiarse al editar</span>
              )}
            </div>

            {/* Estado — solo en edición */}
            {isEditing && (
              <div className={f.field}>
                <label htmlFor="course-status" className={`${f.label} ${f.required}`}>Estado</label>
                <select
                  id="course-status"
                  {...register('status')}
                  className={`${f.select} ${errors.status ? f.inputError : ''}`}
                  aria-describedby={errors.status ? 'course-status-error' : undefined}
                  aria-invalid={!!errors.status}
                >
                  <option value="programmed">Programado</option>
                  <option value="oncurse">En Curso</option>
                  <option value="ended">Finalizado</option>
                  <option value="cancel">Cancelado</option>
                </select>
                {errors.status && (
                  <span id="course-status-error" className={f.fieldError} role="alert">
                    {errors.status.message}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Acciones ── */}
        <div className={f.actions}>
          <Button type="submit" variant="primary" loading={isSubmitting}>
            {isEditing ? 'Actualizar Estado' : 'Activar Ruta'}
          </Button>
          <Button type="button" variant="secondary" onClick={resetForm}>
            Cancelar
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CourseForm;
